import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const mcsFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const usdFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

// Validated categorical pair (CVD dE 32.3, normal-vision dE 38.3, contrast >= 3:1).
const C_UNDER = "#2563eb";
const C_YET = "#d97706";

const BUCKET_LABEL = {
  under_production: "Under Production",
  yet_to_start: "Yet to Start",
};

const URGENCY_LABEL = {
  overdue: "Past ship-by date",
  due_within_15_days: "Due within 15 days",
  due_within_30_days: "Due within 30 days",
  beyond_30_days: "Beyond 30 days",
  no_ship_by_date: "No ship-by date set",
};

// Status styling is paired with a written label everywhere it is used, so
// state is never carried by colour alone.
const URGENCY_STYLE = {
  overdue: "bg-red-50 text-red-700 border-red-200",
  due_within_15_days: "bg-amber-50 text-amber-800 border-amber-200",
  due_within_30_days: "bg-yellow-50 text-yellow-800 border-yellow-200",
  beyond_30_days: "bg-slate-50 text-slate-700 border-slate-200",
  no_ship_by_date: "bg-slate-50 text-slate-500 border-slate-200",
};

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        {accent ? (
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ background: accent }}
          />
        ) : null}
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          {label}
        </div>
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

export default function ConfirmedOrdersProductionStatusDetail() {
  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [bucket, setBucket] = useState("");
  const [urgency, setUrgency] = useState("");
  const [consignee, setConsignee] = useState("");
  const [buyer, setBuyer] = useState("");
  const [country, setCountry] = useState("");

  const [options, setOptions] = useState({
    consignees: [],
    buyers: [],
    countries: [],
  });

  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const requestBody = useMemo(
    () => ({
      from_date: fromDate || "",
      to_date: toDate || "",
      bucket: bucket || "",
      urgency: urgency || "",
      consignee: consignee || "",
      buyer: buyer || "",
      country: country || "",
    }),
    [fromDate, toDate, bucket, urgency, consignee, buyer, country]
  );

  useEffect(() => {
    let active = true;
    api
      .get("/ceo/filter-options")
      .then((res) => {
        if (!active) return;
        const d = res.data || {};
        setOptions({
          consignees: d.consignees || [],
          buyers: d.buyers || [],
          countries: d.countries || [],
        });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    api
      .post(
        "/ai/ceo-chat/confirmed-orders-production-status",
        requestBody,
        { timeout: 120000 }
      )
      .then((res) => {
        if (!active) return;
        setSummary(res.data || null);
      })
      .catch((e) => {
        if (!active) return;
        setSummary(null);
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Confirmed-order production status could not be loaded."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [requestBody]);

  useEffect(() => {
    let active = true;
    setDetailLoading(true);

    api
      .post(
        "/ai/ceo-chat/confirmed-orders-production-status/detail",
        requestBody,
        { timeout: 120000 }
      )
      .then((res) => {
        if (!active) return;
        setDetail(res.data || null);
        setPage(1);
      })
      .catch(() => {
        if (!active) return;
        setDetail(null);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });

    return () => {
      active = false;
    };
  }, [requestBody]);

  const rows = useMemo(() => detail?.rows || [], [detail]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.PoNumber, r.Consignee, r.Buyer, r.Country, r.Plant]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  const exportCsv = useCallback(() => {
    const columns = [
      "PoNumber",
      "PoDate",
      "ShipByDate",
      "DaysToShipBy",
      "Urgency",
      "Bucket",
      "Consignee",
      "Buyer",
      "Country",
      "Plant",
      "GradeLines",
      "OrderedMcs",
      "OrderValue",
      "FirstProductionDate",
      "ProductionRecords",
    ];

    const csv = [
      columns.map(csvEscape).join(","),
      ...filteredRows.map((row) =>
        columns
          .map((c) => {
            if (c === "Bucket") return csvEscape(BUCKET_LABEL[row.Bucket] || row.Bucket);
            if (c === "Urgency") return csvEscape(URGENCY_LABEL[row.Urgency] || row.Urgency);
            return csvEscape(row?.[c]);
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "confirmed-orders-production-status.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredRows]);

  const s = summary?.summary || {};
  const buckets = summary?.buckets || {};
  const underOrders = buckets?.under_production?.orders || 0;
  const yetOrders = buckets?.yet_to_start?.orders || 0;
  const totalOrders = s.total_confirmed_open_orders || 0;
  const underPct = totalOrders ? (underOrders / totalOrders) * 100 : 0;
  const yetPct = totalOrders ? (yetOrders / totalOrders) * 100 : 0;
  const valuePublished = summary?.currency_basis?.value_published;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1700px] space-y-5">
        <div className="flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              Confirmed Orders — Under Production vs Yet to Start
            </h1>
            <p className="mt-2 max-w-4xl text-sm text-slate-600">
              Every confirmed order still in hand — approved in the ERP and not
              yet transported, shipped, delivered or closed — split by whether
              production has actually started against it. An order counts as
              under production when production has been recorded against that
              order in the ERP production register.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/ceo-command-center?tab=production")}
            className="shrink-0 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to Production
          </button>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {/* Filters — one row above the data, applied to summary and drill-down alike */}
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">From (Order Date)</span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2"
              />
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">To (Order Date)</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2"
              />
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Production Status</span>
              <select
                value={bucket}
                onChange={(e) => setBucket(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="">All Orders</option>
                <option value="under_production">Under Production</option>
                <option value="yet_to_start">Yet to Start</option>
              </select>
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Shipment Urgency</span>
              <select
                value={urgency}
                onChange={(e) => setUrgency(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="">Any</option>
                {Object.keys(URGENCY_LABEL).map((k) => (
                  <option key={k} value={k}>
                    {URGENCY_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Consignee</span>
              <select
                value={consignee}
                onChange={(e) => setConsignee(e.target.value)}
                className="max-w-[240px] rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="">All Consignees</option>
                {options.consignees.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Buyer</span>
              <select
                value={buyer}
                onChange={(e) => setBuyer(e.target.value)}
                className="max-w-[240px] rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="">All Buyers</option>
                {options.buyers.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Destination Country</span>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="max-w-[220px] rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="">All Countries</option>
                {options.countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setBucket("");
                setUrgency("");
                setConsignee("");
                setBuyer("");
                setCountry("");
              }}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Reset Filters
            </button>
          </div>

          <div className="mt-3 text-xs text-slate-500">
            Date basis: order date ({summary?.date_range?.date_field || "ScrmPoData.PoDate"}).{" "}
            {summary?.date_range?.note}
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
            Loading confirmed-order production status...
          </div>
        ) : summary ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <StatTile
                label="Confirmed Orders in Hand"
                value={numFmt.format(totalOrders)}
                sub="Approved, not yet dispatched"
              />
              <StatTile
                label="Under Production"
                value={numFmt.format(underOrders)}
                sub={`${underPct.toFixed(1)}% of order book`}
                accent={C_UNDER}
              />
              <StatTile
                label="Yet to Start"
                value={numFmt.format(yetOrders)}
                sub={`${yetPct.toFixed(1)}% of order book`}
                accent={C_YET}
              />
              <StatTile
                label="Ordered Quantity"
                value={mcsFmt.format(s.total_ordered_mcs || 0)}
                sub="MCS across all confirmed orders"
              />
              <StatTile
                label="Order Value"
                value={
                  valuePublished && s.total_order_value != null
                    ? usdFmt.format(s.total_order_value)
                    : "Withheld"
                }
                sub={
                  totalOrders === 0
                    ? "No orders in the current selection"
                    : valuePublished
                      ? "USD — single-currency order book"
                      : "Mixed currency: no approved FX basis"
                }
              />
            </div>

            {/* Proportion meter: two segments, 2px surface gap, legend + direct labels */}
            <div className="rounded-2xl border bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-900">
                  Order Book Split
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-600">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: C_UNDER }} />
                    Under Production
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: C_YET }} />
                    Yet to Start
                  </span>
                </div>
              </div>

              <div className="mt-3 flex h-7 w-full overflow-hidden rounded-lg bg-slate-100">
                <div
                  className="h-full rounded-l-lg"
                  style={{ width: `${underPct}%`, background: C_UNDER }}
                  title={`Under Production: ${underOrders}`}
                />
                <div style={{ width: 2, background: "#ffffff" }} />
                <div
                  className="h-full rounded-r-lg"
                  style={{ width: `${yetPct}%`, background: C_YET }}
                  title={`Yet to Start: ${yetOrders}`}
                />
              </div>

              <div className="mt-2 flex justify-between text-xs text-slate-600">
                <span>
                  {numFmt.format(underOrders)} orders under production
                </span>
                <span>
                  {numFmt.format(yetOrders)} orders not yet started
                </span>
              </div>
            </div>

            {/* Exception view */}
            <div className="rounded-2xl border bg-white p-5 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">
                Not Yet Started — by Shipment Deadline
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Confirmed orders with no production recorded, ranked by how
                close they are to their ship-by date.
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Deadline Position</th>
                      <th className="py-2 pr-4 text-right">Orders</th>
                      <th className="py-2 pr-4 text-right">Ordered MCS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summary.urgency || []).map((u) => (
                      <tr key={u.urgency} className="border-b last:border-0">
                        <td className="py-2 pr-4">
                          <span
                            className={`inline-block rounded-lg border px-2 py-0.5 text-xs font-medium ${
                              URGENCY_STYLE[u.urgency] || "border-slate-200 bg-slate-50 text-slate-700"
                            }`}
                          >
                            {URGENCY_LABEL[u.urgency] || u.urgency}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-right font-medium text-slate-900">
                          {numFmt.format(u.orders)}
                        </td>
                        <td className="py-2 pr-4 text-right text-slate-700">
                          {mcsFmt.format(u.ordered_mcs)}
                        </td>
                      </tr>
                    ))}
                    {!(summary.urgency || []).length ? (
                      <tr>
                        <td className="py-3 text-slate-500" colSpan={3}>
                          No orders are pending production start.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Drill-down */}
            <div className="rounded-2xl border bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Order-level Detail
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {detailLoading
                      ? "Loading orders..."
                      : `${numFmt.format(filteredRows.length)} order(s)` +
                        (detail?.truncated ? " (row limit reached)" : "")}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search PO, consignee, buyer, country, plant"
                    className="w-72 rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={exportCsv}
                    disabled={!filteredRows.length}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">PO Number</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Consignee</th>
                      <th className="py-2 pr-4">Buyer</th>
                      <th className="py-2 pr-4">Country</th>
                      <th className="py-2 pr-4">Plant</th>
                      <th className="py-2 pr-4">Order Date</th>
                      <th className="py-2 pr-4">Ship By</th>
                      <th className="py-2 pr-4">Deadline</th>
                      <th className="py-2 pr-4 text-right">Ordered MCS</th>
                      <th className="py-2 pr-4 text-right">Order Value</th>
                      <th className="py-2 pr-4">Production Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r) => (
                      <tr key={r.PoID} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 pr-4 font-medium text-slate-900">{r.PoNumber}</td>
                        <td className="py-2 pr-4">
                          <span className="flex items-center gap-1.5">
                            <span
                              aria-hidden="true"
                              className="inline-block h-2 w-2 rounded-full"
                              style={{
                                background:
                                  r.Bucket === "under_production" ? C_UNDER : C_YET,
                              }}
                            />
                            {BUCKET_LABEL[r.Bucket] || r.Bucket}
                          </span>
                        </td>
                        <td className="py-2 pr-4">{r.Consignee}</td>
                        <td className="py-2 pr-4">{r.Buyer}</td>
                        <td className="py-2 pr-4">{r.Country}</td>
                        <td className="py-2 pr-4">{r.Plant}</td>
                        <td className="py-2 pr-4">{r.PoDate || "—"}</td>
                        <td className="py-2 pr-4">{r.ShipByDate || "—"}</td>
                        <td className="py-2 pr-4">
                          <span
                            className={`inline-block rounded-lg border px-2 py-0.5 text-xs ${
                              URGENCY_STYLE[r.Urgency] || "border-slate-200 bg-slate-50 text-slate-700"
                            }`}
                          >
                            {URGENCY_LABEL[r.Urgency] || r.Urgency}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-right">{mcsFmt.format(r.OrderedMcs || 0)}</td>
                        <td className="py-2 pr-4 text-right">
                          {r.OrderValue == null ? "—" : usdFmt.format(r.OrderValue)}
                        </td>
                        <td className="py-2 pr-4">
                          {r.FirstProductionDate
                            ? `${r.FirstProductionDate} (${numFmt.format(r.ProductionRecords)} record${
                                r.ProductionRecords === 1 ? "" : "s"
                              })`
                            : "Not started"}
                        </td>
                      </tr>
                    ))}
                    {!pageRows.length && !detailLoading ? (
                      <tr>
                        <td className="py-4 text-center text-slate-500" colSpan={12}>
                          No confirmed orders match the current filters.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              {pageCount > 1 ? (
                <div className="mt-3 flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-xl border border-slate-300 px-3 py-1.5 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="text-slate-600">
                    Page {page} of {pageCount}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={page >= pageCount}
                    className="rounded-xl border border-slate-300 px-3 py-1.5 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              ) : null}

              {detail?.totals ? (
                <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                  Drill-down totals — {numFmt.format(detail.totals.orders)} orders ·{" "}
                  {numFmt.format(detail.totals.under_production_orders)} under production ·{" "}
                  {numFmt.format(detail.totals.yet_to_start_orders)} yet to start ·{" "}
                  {mcsFmt.format(detail.totals.ordered_mcs)} MCS
                  {detail.totals.order_value != null
                    ? ` · ${usdFmt.format(detail.totals.order_value)}`
                    : ""}
                </div>
              ) : null}
            </div>

            {/* Validation / lineage */}
            <div className="rounded-2xl border bg-white p-5 text-xs text-slate-600 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">
                Validation &amp; ERP Reconciliation
              </div>
              <ul className="mt-2 space-y-1">
                <li>
                  <span className="font-medium text-slate-800">Reconciliation:</span>{" "}
                  {summary.reconciliation?.pass ? "Passed" : "Failed"} — {summary.reconciliation?.equation}
                </li>
                <li>
                  <span className="font-medium text-slate-800">Compatibility:</span>{" "}
                  {summary.compatibility?.pass ? "Passed" : "Failed"} — orders with duplicate shipment rows:{" "}
                  {summary.compatibility?.multi_brief_orders}; unresolved production links:{" "}
                  {summary.compatibility?.ambiguous_production_links}
                </li>
                <li>
                  <span className="font-medium text-slate-800">Production history covers:</span>{" "}
                  {summary.coverage?.production_data_from || "—"} to{" "}
                  {summary.coverage?.production_data_to || "—"}
                  {summary.coverage?.orders_before_production_history
                    ? ` · ${summary.coverage.orders_before_production_history} order(s) predate this history and may show as not started`
                    : " · no orders predate this history"}
                </li>
                <li>
                  <span className="font-medium text-slate-800">Validation status:</span>{" "}
                  {summary.validation_status === "business_validation_pending"
                    ? "Business validation pending — awaiting client sign-off on the production-start definition"
                    : summary.validation_status}
                </li>
              </ul>

              {(summary.notes || []).length ? (
                <div className="mt-3 border-t pt-3">
                  <div className="font-medium text-slate-800">Notes</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    {summary.notes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
